import type { ExpectedNote, FretPosition, PitchClass, StringNumber } from '@/types/music';

/** A4 reference. Everything else is derived from it. */
export const A4_HZ = 440;
export const A4_MIDI = 69;

/**
 * Standard tuning, written low-to-high the way the frets arrays are written:
 * index 0 = string 6 (low E2) … index 5 = string 1 (high E4).
 */
export const STANDARD_TUNING_MIDI = [40, 45, 50, 55, 59, 64] as const;

export const STRING_NAMES = ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'] as const;

/** Array index (0 = low E) → guitarist's string number (6 = low E). */
export function indexToStringNumber(index: number): StringNumber {
  return (6 - index) as StringNumber;
}

/** Guitarist's string number → array index. */
export function stringNumberToIndex(str: StringNumber): number {
  return 6 - str;
}

export function midiToFrequency(midi: number): number {
  return A4_HZ * Math.pow(2, (midi - A4_MIDI) / 12);
}

export function frequencyToMidi(hz: number): number {
  return A4_MIDI + 12 * Math.log2(hz / A4_HZ);
}

export function midiToPitchClass(midi: number): PitchClass {
  return (((midi % 12) + 12) % 12) as PitchClass;
}

export const PITCH_CLASS_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

/**
 * Resolve the notes a fingering actually sounds. Muted strings are dropped, so
 * the result is exactly what the microphone should hear.
 */
export function expectedNotes(frets: readonly FretPosition[]): ExpectedNote[] {
  const notes: ExpectedNote[] = [];
  for (let i = 0; i < frets.length; i++) {
    const fret = frets[i];
    if (fret === null || fret === undefined) continue;
    const midi = STANDARD_TUNING_MIDI[i] + fret;
    notes.push({
      string: indexToStringNumber(i),
      fret,
      midi,
      frequency: midiToFrequency(midi),
      pitchClass: midiToPitchClass(midi),
    });
  }
  return notes;
}

/** Distinct pitch classes a fingering sounds, low to high, without duplicates. */
export function expectedPitchClasses(frets: readonly FretPosition[]): PitchClass[] {
  const seen = new Set<PitchClass>();
  for (const note of expectedNotes(frets)) seen.add(note.pitchClass);
  return [...seen].sort((a, b) => a - b);
}

/** The lowest sounding note of a fingering — the chord's actual bass. */
export function bassNote(frets: readonly FretPosition[]): ExpectedNote | null {
  const notes = expectedNotes(frets);
  if (notes.length === 0) return null;
  return notes.reduce((lowest, n) => (n.midi < lowest.midi ? n : lowest));
}

/** Distance in cents between two frequencies. */
export function centsBetween(a: number, b: number): number {
  return 1200 * Math.log2(a / b);
}
